"use client";

// ─────────────────────────────────────────────────────────────────────────────
// useClientBuddyScript — drives the BuddyOverlay without an SSE backend.
//
// The replay flow emits buddy beats from the server as `2:[{type:"buddy_beat",
// ...}]` SSE frames; the chat.tsx buddy-gate release POSTs back to
// `/replay/buddy-gate/release`. That model doesn't fit pages like
// /alerts/[id] where there's no streaming session — the tour is just
// a fixed client-side sequence of beats.
//
// This hook lets any page run a tour against BuddyOverlay with a
// static list of beats:
//
//   const { beat, onContinue, onExit, done } = useClientBuddyScript(
//     ALERT_TOUR_BEATS,
//     { onComplete: stripOnboardingParam },
//   );
//   return (
//     <BuddyOverlay beat={beat} onContinue={onContinue} onExit={onExit} />
//   );
//
// Semantics:
//   • `beat` is the CURRENT beat, or null when the tour is finished /
//     the viewer hit Esc / the onExit bubble button fired.
//   • `onContinue(beatId)` advances to the next beat. Matches the
//     replay's gate-release contract so BuddyOverlay is 100% reusable
//     (the overlay itself only cares about the beat shape + callbacks).
//   • `onExit()` ends the tour immediately. Wired to Esc + overlay
//     click-outside by BuddyOverlay. Fires `onComplete` so callers
//     can do one-time cleanup (strip URL params, write local-storage
//     "seen" flag, log telemetry).
//   • `done` is `true` once the tour ended (either naturally or via
//     exit). Callers can use this to conditionally unmount /
//     short-circuit re-renders.
//
// Design notes:
//   - NO re-start semantics. Each `useClientBuddyScript` invocation
//     represents one tour run. To replay, remount the host
//     component (e.g. via a `key` prop).
//   - The beats array is captured at mount and frozen for the
//     lifetime of the hook. Mutating the array later will not take
//     effect. This is intentional: tour content shouldn't shift
//     mid-tour (would look buggy); pass a stable reference from the
//     host.
//   - The `beatId` parameter on `onContinue` is unused here but kept
//     in the signature so BuddyOverlay's existing contract is
//     honoured without adapters. Useful for future per-beat
//     telemetry.
// ─────────────────────────────────────────────────────────────────────────────

import { useCallback, useRef, useState } from "react";
import type { BuddyBeat } from "./BuddyOverlay";

export interface UseClientBuddyScriptOptions {
  /** Called once when the tour ends NATURALLY — i.e. the user
   *  advanced past the final beat. Use this for "tour finished
   *  successfully" side-effects like chained navigation (handoff
   *  to a follow-up tour). NOT called when the user dismisses via
   *  Esc / outside-click — see `onExit` for that. */
  onComplete?: () => void;
  /** Called once when the user DISMISSES the tour mid-flow (Esc
   *  key, outside click, or any path that calls `onExit`). Use
   *  this for "respect-the-dismissal" cleanup like stripping URL
   *  params without navigating. If unset, `onComplete` is fired
   *  as a fallback to preserve backwards compatibility with
   *  callers written before the split. */
  onExit?: () => void;
  /** Called on every beat advance. `nextIndex` is the 0-based index
   *  of the beat that just became active (i.e. AFTER the advance).
   *  When the tour ends it is `beats.length`. Useful for telemetry
   *  or pre-loading state for the next step. */
  onAdvance?: (nextIndex: number) => void;
}

export interface UseClientBuddyScriptResult {
  /** Current beat, or null when the tour is finished / exited. Pass
   *  straight to BuddyOverlay. */
  beat: BuddyBeat | null;
  /** Advance to the next beat. Contract matches BuddyOverlay's
   *  `onContinue` prop — takes the beatId the overlay just
   *  dismissed (we ignore it). */
  onContinue: (beatId: string) => void;
  /** End the tour immediately. Contract matches BuddyOverlay's
   *  `onExit` prop. */
  onExit: () => void;
  /** True after the tour ends (either completed or exited). */
  done: boolean;
  /** Zero-based index of the currently-active beat. Useful for
   *  debugging + external "step N of M" progress indicators. Beyond
   *  the last index once the tour is done. */
  index: number;
}

export function useClientBuddyScript(
  beats: BuddyBeat[],
  options?: UseClientBuddyScriptOptions,
): UseClientBuddyScriptResult {
  const [index, setIndex] = useState(0);
  const [done, setDone] = useState(false);

  // Capture callbacks in a ref so consumers aren't forced to
  // memoize `options`. The hook itself stays referentially stable
  // across parent re-renders.
  const optionsRef = useRef<UseClientBuddyScriptOptions | undefined>(options);
  optionsRef.current = options;

  // Guard against double-firing terminal callbacks (e.g. rapid
  // Enter + Esc in quick succession could schedule two advances).
  // The same flag covers both onComplete and onExit because only
  // ONE terminal event ever fires per tour run.
  const completedRef = useRef(false);
  const fireTerminal = useCallback((kind: "complete" | "exit") => {
    if (completedRef.current) return;
    completedRef.current = true;
    try {
      const opts = optionsRef.current;
      if (kind === "complete") {
        opts?.onComplete?.();
      } else {
        // Prefer onExit when the caller wired one up. Fall back to
        // onComplete so older callers that only set onComplete still
        // get cleanup signal — preserves backwards compatibility.
        if (opts?.onExit) {
          opts.onExit();
        } else {
          opts?.onComplete?.();
        }
      }
    } catch (err) {
      // Never let a caller's terminal callback throw unwind the
      // tour — the overlay is already unmounting; log and move on.
      // eslint-disable-next-line no-console
      console.error("[useClientBuddyScript] terminal callback threw", err);
    }
  }, []);

  const onContinue = useCallback(
    (beatId: string) => {
      if (completedRef.current) return;
      setIndex((current) => {
        const currentBeat = beats[current];
        if (currentBeat?.beatId && currentBeat.beatId !== beatId) {
          return current;
        }
        const next = current + 1;
        if (next >= beats.length) {
          setDone(true);
          fireTerminal("complete");
          return beats.length; // sentinel past-last
        }
        try {
          optionsRef.current?.onAdvance?.(next);
        } catch {
          /* onAdvance is best-effort — ignore */
        }
        return next;
      });
    },
    [beats.length, fireTerminal],
  );

  const onExit = useCallback(() => {
    if (completedRef.current) return;
    setDone(true);
    fireTerminal("exit");
  }, [fireTerminal]);

  const beat = done || index >= beats.length ? null : beats[index] ?? null;

  return { beat, onContinue, onExit, done, index };
}
